/**
 * Pure template helpers for admin-authored public record layouts.
 *
 * No React, no DOM, no network — see renderRecordTemplate.js for the module
 * that walks a parsed document, and PublicCustomLayout.js for the component
 * that mounts the result. Keeping resolution pure is what makes the escaping
 * rules in renderRecordTemplate.js testable in isolation.
 *
 * Admin templates are authored by tenant users, so path resolution is a trust
 * boundary: it walks ONLY own enumerable properties of plain objects. Reaching
 * `constructor`, `__proto__` or any inherited method would hand an author a
 * route from a template string to live JS objects, so every hop is checked
 * with Object.prototype.hasOwnProperty rather than a bare `in` or `?.`.
 */

import {
  formatInstantDate, formatInstantTime, parseInstant, zoneLabel, zoneOf,
} from './utils';

const hasOwn = Object.prototype.hasOwnProperty;

/**
 * Walks a dotted path against the resolved data tree.
 *
 * Returns `undefined` for anything unreachable — a missing root, a missing
 * leaf, a hop through null, or an inherited property. Present-but-falsy values
 * (0, false, '') resolve to themselves; callers must not treat them as missing.
 */
export function resolvePath(data, path) {
  if (!data || typeof path !== 'string') return undefined;

  const segments = path.split('.');
  let current = data;

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    if (!hasOwn.call(current, segment)) return undefined;
    current = current[segment];
  }

  return current;
}

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

/**
 * The zone one placeholder's datetime should be read in.
 *
 * Looked up as a sibling of the value itself — `event.start_datetime` asks
 * `event.timezone` — because that is where the column lives. A related block
 * has no zone of its own (`registration.registration_start_datetime` belongs to
 * the event that owns it), so the record the page is drawing answers for it.
 *
 * Returns undefined when nothing declares a zone, which leaves the viewer's own
 * in force — correct for a table that stores no place, like a created_at.
 */
export function timeZoneForPath(data, path, recordAlias) {
  const segments = String(path || '').split('.');
  if (segments.length > 1) {
    const sibling = zoneOf(resolvePath(data, segments.slice(0, -1).join('.')));
    if (sibling) return sibling;
  }
  if (recordAlias) {
    const onRecord = zoneOf(resolvePath(data, recordAlias));
    if (onRecord) return onRecord;
  }
  return zoneOf(data);
}

/**
 * Applies one named filter. The filter set is deliberately closed — no
 * arbitrary expressions, no method calls. An unknown filter is a no-op rather
 * than an error, so a template stays renderable while an author is editing it;
 * the admin-side validator is where typos are reported.
 */
export function applyFilter(value, name, arg, timeZone) {
  switch (name) {
    case 'date':
    case 'datetime': {
      if (isBlank(value)) return value;
      const date = parseInstant(value);
      if (!date) return value;
      const formatted = formatInstantDate(date, arg, timeZone);
      if (name === 'datetime') {
        return `${formatted} ${formatInstantTime(date, timeZone)}`;
      }
      return formatted;
    }
    /* "IST" / "BST" / "GMT+5:30" for the zone this value belongs to, so a
       published start time is not left ambiguous. Spelled in the reader's
       locale; the zone itself is always the record's own. */
    case 'zone': {
      if (isBlank(value)) return value;
      const date = parseInstant(value);
      if (!date) return '';
      return zoneLabel(date, timeZone);
    }
    case 'currency': {
      if (isBlank(value)) return value;
      const num = Number(value);
      return Number.isNaN(num) ? value : num.toFixed(2);
    }
    case 'number': {
      if (isBlank(value)) return value;
      const num = Number(value);
      return Number.isNaN(num) ? value : num.toLocaleString();
    }
    case 'truncate': {
      const limit = parseInt(arg, 10);
      const str = String(value ?? '');
      if (!Number.isFinite(limit) || str.length <= limit) return value;
      return `${str.slice(0, limit)}…`;
    }
    case 'default':
      return isBlank(value) ? arg : value;
    case 'lower':
      return String(value ?? '').toLowerCase();
    case 'upper':
      return String(value ?? '').toUpperCase();
    default:
      return value;
  }
}

// {{ path }} | {{ path | filter }} | {{ path | filter:arg }} | {{ path | filter:"arg" }}
const PLACEHOLDER_RE = /\{\{\s*([^}|]+?)\s*(?:\|\s*([a-zA-Z_]+)\s*(?::\s*("[^"]*"|'[^']*'|[^}\s]+)\s*)?)?\}\}/g;

/** True when the string contains at least one {{ }} placeholder. */
export function hasPlaceholder(str) {
  if (typeof str !== 'string' || str === '') return false;
  PLACEHOLDER_RE.lastIndex = 0;
  return PLACEHOLDER_RE.test(str);
}

function stripQuotes(arg) {
  if (typeof arg !== 'string') return arg;
  const first = arg[0];
  const last = arg[arg.length - 1];
  if (arg.length >= 2 && (first === '"' || first === "'") && first === last) {
    return arg.slice(1, -1);
  }
  return arg;
}

/**
 * Replaces every {{ }} placeholder in `str` with its resolved value.
 *
 * Substitution is single-pass by construction: String.replace walks the
 * original string and never rescans what it wrote, so a record whose value
 * happens to contain "{{ event.price }}" is emitted literally rather than
 * expanded. A recursive implementation would let one field's contents address
 * another field — an author-visible information leak across the record.
 *
 * An unresolvable reference becomes an empty string (spec §5.4): quiet
 * degradation is what lets a failed related-data block disappear cleanly
 * instead of printing "undefined". Genuine typos are caught by the admin-side
 * validator at save time, where the feedback is actionable.
 *
 * Returns a STRING. Escaping is not this function's job — renderRecordTemplate
 * assigns the result via textContent, which is the actual XSS boundary.
 */
export function interpolate(str, data, { recordAlias } = {}) {
  if (typeof str !== 'string' || str === '') return str;

  return str.replace(PLACEHOLDER_RE, (_match, path, filterName, filterArg) => {
    const trimmedPath = path.trim();
    let value = resolvePath(data, trimmedPath);

    if (filterName) {
      value = applyFilter(
        value, filterName, stripQuotes(filterArg),
        timeZoneForPath(data, trimmedPath, recordAlias),
      );
    }

    if (value === null || value === undefined) return '';
    return String(value);
  });
}
